<template>
	<div class="kmc-solicitor-search">

		<div class="uk-form-label uk-display-block" v-bind:for="name">
			{{ label }}
		</div>
		<div class="kmc-field-help-text" v-if="helpText">
			<p v-html="helpText"></p>
		</div>
		<div class="uk-flex uk-flex-row uk-flex-middle uk-margin-top" v-if="!inputValue">
			<div class="uk-flex-1" v-bind:for="name">
				<div class="kmc-input kmc-input-autocomplete uk-margin-remove">
					<input ref="input"
						   type="search"
						   :value="textInputValue"
						   @input="handleInput($event.target.value)"
						   @keyup.enter="onSearch"
						   @focus="onFocus"
						   :disabled="loading"
						   :minlength="4"
						   :maxlength="200" />
					<div class="border"></div>
					<div v-if="loading" uk-spinner style="position: absolute; right: 10px; height: 30px; width: 30px; top: 10px; z-index: 100"></div>
				</div>
			</div>

			<a class="uk-button uk-button-large uk-button-primary uk-margin-left" :disabled="loading" @click="onSearch">
				Search
			</a>
		</div>

		<div v-if="inputValue" class="uk-margin-top uk-flex uk-flex-bottom">

			<div class="uk-flex-1 kmc-color-dark-2">
				<div class="uk-text-bold">{{ inputValue.name }}</div>
				<div>{{ inputValue['address1'] }}, {{ inputValue['address2'] }} {{ inputValue['address3'] }}</div>
				<div>{{ inputValue['town'] }} {{ inputValue['postcode'] }}</div>
			</div>

			<a class="uk-button uk-button-primary uk-margin-small-top uk-margin-right" @click="change">
				Change
			</a>

		</div>


		<div v-if="shouldShowResults">
			<div class="uk-text-small uk-margin-small">
				Found
				<strong>{{ options.length }}</strong> records
			</div>

			<div class="kmc-autocomplete-items-container kmc-color-dark-2">
				<div v-for="r in options" class="uk-flex uk-flex-column kmc-autocomplete-item uk-text-small" @click="clickItem(r)">
					<span class="uk-text-bold">{{ r.name }}</span>
					<span class="kmc-color-dark">{{ r['address1'] }}, {{ r['address2'] }}</span>
					<span class="kmc-color-dark">{{ r['town'] }} {{ r['postcode'] }} </span>
				</div>
			</div>
		</div>

	</div>
</template>

<script>
	import { computed, onMounted, ref, watch } from 'vue'
	import { KmcInputCheckbox, KmcInputPostcode, KmcInputText } from "./index"
	import { useField } from "vee-validate"

	export default {
		name: 'KmcSolicitorSearch',
		components: {},
		props: {
			name: {
				type: String,
			},
			value: {},
			modelValue: {},
			textInputValue: {},
			helpText: {
				type: String,
			},
			label: {
				type: String,
			}
		},
		emits: [
			'input',
			'search',
			'select',
			'edit',
			'update:modelValue',
			'update:textInputValue',
			'setSolicitorErrorMessage'
		],
		beforeCreate() {
			this.$options.components.KmcInputText = KmcInputText
			this.$options.components.KmcInputPostcode = KmcInputPostcode
			this.$options.components.KmcInputCheckbox = KmcInputCheckbox
		},
		setup(props, context) {

			const input = ref()
			const showResults = ref(false)
			const loading = ref(false)
			const options = ref([])

			const {
				value: inputValue,
				handleChange,
				handleBlur,
				meta,
			} = useField(props.name, undefined, {
				initialValue: props.value,
			})

			onMounted(() => {
			})


			watch(() => props.textInputValue, (current, previous) => {
				handleInput(current)
			})

			function handleInput(value) {
				var pattern = /[^0-9A-Za-z\s&]/g 
				value = value.replace(pattern, '')
				if (input.value) {
					input.value.value = value
				}
				if (!isNaN(value) && value.length > 7) {					
					value = value.substring(0, 7);
					input.value.value = value;
				}
				context.emit('update:textInputValue', value)
				hideResults()
				if (value === '') {
					handleChange(null)
					options.value = []
				}
			}

			function clickItem(data) {
				hideResults()
                context.emit('setSolicitorErrorMessage', '');
				handleChange(data)
				context.emit('update:modelValue', data)
				context.emit('select', data)
			}

			function displayResults() {
				showResults.value = true
			}

			function hideResults() {
				showResults.value = false
			}

			let shouldShowResults = computed(() => {
				return showResults.value && (options.value.length > 0)
			})

			const onSearch = function () {
				if (loading.value) {
					return
				}

				hideResults()
				// const val =  parseInt(input.value.value)
				const val = input.value.value

				if (!val || val.trim().length < 4) {
                    context.emit('setSolicitorErrorMessage', 'Enter a correct company name/number (minimum 4 characters) or select one of the options below');
					return
				}

				const search = new URLSearchParams({ searchTerm: val.trim().toString() })

				loading.value = true
                context.emit('setSolicitorErrorMessage', '');

				fetch(`${location.pathname}/SolicitorSearch?${search}`).then(async response => {
					const data = await response.json()
					if (!response.ok) {
						const error = (data && data.message) || response.statusText
						return Promise.reject(error)
					}

					if (data['errors']) {
						return Promise.reject(data['errors'])
					}

					const result = processApiResults(data)

					if (!result.length) {
						return Promise.reject()
					}

					options.value = result

					displayResults()

				}).catch(error => {
					loading.value = false
                    context.emit('setSolicitorErrorMessage', error ? 'We are currently experiencing a technical difficulty, please try again later' : 'No results found');
				}).finally(() => {
					loading.value = false
				})
			}

			const processApiResults = function (data) {
				let results = []
				let organisations = data['organisations']

				organisations = organisations.filter(o => {
					return o.id
				})

				organisations.forEach(organisation => {
					organisation['offices'].forEach(office => {

						let tradingNames = organisation['tradingNames'].filter(name => name.trim() !== '')
						tradingNames.push(organisation['practiceName'])

						tradingNames.forEach(name => {
							results.push({
								...office,
								name: name,
								organisationId: organisation['id'],
								licenceNumber: organisation['licenceNumber'],
								practiceName: organisation['practiceName'],
								displayValue: [name, office['address1'], office['postcode']].join(', '),
								isVerified: true
							})
						})
					})
				})

				return results.sort((a, b) => (a.name.trim().toUpperCase() > b.name.trim().toUpperCase()) ? 1 : -1)
			}

			const onFocus = function (e) {
				if (options.value.length > 0) {
					displayResults()
				}
			}

			const change = function () {
				handleChange(null)
				displayResults()
			}

			return {
				inputValue,
				loading,
				showResults,
				input,
				handleInput,
				onSearch,
				clickItem,
				options,
				shouldShowResults,
				onFocus,
				change,
			}
		}
	}
</script>

